<template>
  <div id="app-container"
       class="min-h-screen bg-background">

    <NavBar :mode="'hifi'" />
    <RouterView />

    <!-- Toast Notifications -->
    <TransitionGroup tag="div"
                     name="toast"
                     class="fixed top-[64px] right-4 z-50 space-y-2 pointer-events-none"
                     @enter="onToastEnter"
                     @leave="onToastLeave">
      <div v-for="toast in toasts"
           :key="toast.id"
           :class="[
             'p-4 rounded-lg shadow-lg pointer-events-auto max-w-sm',
             getToastClasses(toast.type),
           ]">
        <div class="flex items-center gap-3">
          <i :class="getToastIcon(toast.type)"></i>
          <span class="font-medium">{{ toast.message }}</span>
        </div>
      </div>
    </TransitionGroup>
    <SiteFooter />
  </div>
</template>

<script setup lang="ts">
import { useToast } from '@/composables/useToast';
import { useCartStore } from '@/stores/cartStore';
import { useAuthStore } from '@/stores/authStore';
import NavBar from '@/components/layout/header/NavBar.vue';
import SiteFooter from '@/components/layout/SiteFooter.vue';

const { toasts } = useToast();
const cartStore = useCartStore();
const authStore = useAuthStore();

// Hydrate cart from localStorage on app init
cartStore.hydrate();
authStore.hydrate();

const getToastClasses = (type: string): string => {
  switch (type) {
    case 'success':
      return 'bg-green-50 text-green-800 border border-green-200';
    case 'error':
      return 'bg-red-50 text-red-800 border border-red-200';
    case 'warning':
      return 'bg-yellow-50 text-yellow-800 border border-yellow-200';
    case 'info':
    default:
      return 'bg-blue-50 text-blue-800 border border-blue-200';
  }
};

const getToastIcon = (type: string): string => {
  switch (type) {
    case 'success':
      return 'fas fa-check-circle text-green-600';
    case 'error':
      return 'fas fa-exclamation-circle text-red-600';
    case 'warning':
      return 'fas fa-exclamation-triangle text-yellow-600';
    case 'info':
    default:
      return 'fas fa-info-circle text-blue-600';
  }
};

const onToastEnter = (el: Element) => {
  // Animation handled by CSS
};

const onToastLeave = (el: Element) => {
  // Animation handled by CSS
};
</script>

<style scoped lang="scss">
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}
.toast-enter-active {
  animation: slide-down 0.3s ease-out;
}

.toast-leave-active {
  animation: slide-up 0.3s ease-out;
}

</style>
